import { functions } from "@/main";

/**
 * @module GoogleGeocodeGateway
 *
 * The one place IRES Office issues a Geocoding request, and it goes through IRES Cloud.
 *
 * **Why it cannot be called from the page.** The Geocoding web service refuses a key carrying HTTP
 * referrer restrictions outright (`REQUEST_DENIED: API keys with referer restrictions cannot be
 * used with this API`, measured 2026-09-14); the web service APIs accept only IP restrictions,
 * which a browser cannot satisfy. So a page that calls `geocode/json` itself must ship an
 * **unrestricted** key, which anyone can lift out of the bundle with the Network tab open and bill
 * to us. Going through Cloud lets the browser key be referrer-locked down to the Maps JavaScript
 * library, which does accept such a key.
 *
 * **What deliberately did not move.** Only the transport. Every scoring, cross-checking, candidate
 * and outcome decision stays in `@/utils/GeocodingService`, where it is covered by
 * `tests/unit/geocodingTransport.spec.ts` — the same tests, unchanged, before and after this module
 * stopped calling Google directly. That is what makes the change incapable of altering a result.
 *
 * Imports nothing from `GeocodingService`, so there is no import cycle and a test can mock this
 * module without pulling the service's dependencies in with it.
 */

/**
 * The Cloud Function that fronts the Geocoding API.
 *
 * Swap to `googleGeocodeDevelopment` **locally only** to drive the development twin from a dev
 * server without touching the function released clients call.
 */
const GEOCODE_CALLABLE = "googleGeocode";

/**
 * Longer than the 5s Cloud allows itself for the Google call, so a slow lookup comes back as
 * Google's own failure rather than as a callable that gave up first.
 */
const GEOCODE_TIMEOUT_MS = 10000;

/**
 * The Geocoding API parameters IRES Office uses.
 *
 * Google's own snake_case names, so a request reads the same here as in its documentation. Cloud
 * validates them against the same list before forwarding.
 */
export interface GoogleGeocodeParams {
  /** Forward lookup: the address as one string. */
  address?: string;
  /** Reverse lookup: `"{latitude},{longitude}"`. */
  latlng?: string;
  /** Lookup by Google's own identifier for a place. */
  // eslint-disable-next-line @typescript-eslint/camelcase
  place_id?: string;
  /** Hard filter, e.g. `"country:DE"`. */
  components?: string;
  /** Soft ranking bias used when no country filter can be built. */
  region?: string;
  /** Narrows a reverse lookup, e.g. `"street_address"`. */
  // eslint-disable-next-line @typescript-eslint/camelcase
  result_type?: string;
  /** Locale for `formatted_address` and the component long names. */
  language?: string;
}

/** As much of Google's response as this app reads. */
export interface GoogleGeocodeResponse {
  /** `"OK"`, `"ZERO_RESULTS"`, `"REQUEST_DENIED"`, and the rest of Google's vocabulary. */
  status?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results?: any[];
}

/** What the callable resolves with: Google's body, or a failure the caller has to react to. */
interface GeocodeProxyResult {
  ok?: boolean;
  data?: GoogleGeocodeResponse;
  statusCode?: number;
  apiMessage?: string;
}

/**
 * Issues one Geocoding request and returns Google's response body.
 *
 * Google's own `status` is preserved, non-`OK` values included, because the callers distinguish
 * them: `ZERO_RESULTS` is stored as "this address has no position", while `REQUEST_DENIED` has to
 * be retried rather than buried.
 *
 * **Throws on a transport failure** rather than swallowing it, because each caller in
 * `GeocodingService` already decides for itself what a failed lookup means: a cross-check that
 * cannot run is `UNKNOWN`, a forward lookup that cannot run is `FAILED`, and a reverse lookup that
 * cannot run is simply no address to show. Collapsing those here would lose the distinction.
 *
 * @param params - the request parameters, without the key.
 * @returns Google's response body, unparsed and unjudged.
 */
export async function googleGeocode(
  params: GoogleGeocodeParams
): Promise<GoogleGeocodeResponse> {
  const callable = functions.httpsCallable(GEOCODE_CALLABLE, {
    timeout: GEOCODE_TIMEOUT_MS,
  });

  const result = (await callable({ params })).data as GeocodeProxyResult | undefined;

  if (result?.ok !== true) {
    throw new Error(result?.apiMessage ?? "Geocoding request failed");
  }

  return result.data ?? {};
}
